import React, { Fragment } from 'react';
import { Orientation, OrientationProp } from '../common-types';

/**
 * Type for a label position.
 */
export enum StepperLabelPosition {
  NORMAL = 'normal',
  REVERSED = 'reversed'
}

/**
 * Params for {@link StepperProps} property
 */
export interface StepParams {
  /**
   * Visible part for step
   */
  label: string;
  /**
   * ID of step
   */
  value: string;
}

/**
 * Props for {@link Step}
 */
export interface StepProps extends StepParams {
  /**
   * Step is activated
   */
  active: boolean;
  /**
   * Custom inner content
   */
  children?: (props: StepProps) => number | string | JSX.Element;
}

/**
 * Props for {@link Stepper}
 */
export interface StepperProps extends OrientationProp {
  /**
   * Steps for the stepper
   */
  steps: StepParams[];
  /**
   * Active step from {@link steps}
   */
  current: string;
  /**
   * {@link Step} customization
   */
  children?: StepProps['children'];
  /**
   * Position of the label.
   */
  labelPosition?: StepperLabelPosition;
}

/**
 * Item component for Stepper component.
 *
 * @param children custom step content {@link CallableFunction}
 * @param props the {@link StepProps}
 * @return the created {@link JSX.Element}
 * @constructor
 * @author boris.brtan
 */
export function Step({ children, ...props }: StepProps): JSX.Element {
  const className = ['step', props.active && 'active']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      {children ? children(props) : <span>{props.label}</span>}
    </div>
  );
}

/**
 * Component for displaying separated steps.
 *
 * @param orientation the {@link Orientation}
 * @param labelPosition the {@link StepperLabelPosition}
 * @param props the {@link StepperProps}
 * @return the created {@link JSX.Element}
 * @constructor
 * @author boris.brtan
 */
export function Stepper({
  orientation = Orientation.VERTICAL,
  labelPosition = StepperLabelPosition.NORMAL,
  ...props
}: StepperProps): JSX.Element {
  const classNames = ['ehs-stepper', orientation];
  if (labelPosition === StepperLabelPosition.REVERSED) {
    classNames.push('stepper-label-reversed');
  }

  return (
    <div className={classNames.join(' ')}>
      {props.steps.map(
        (step: StepParams, index: number, steps: StepParams[]) => (
          <Fragment key={step.value}>
            <Step {...step} active={props.current === step.value}>
              {props.children}
            </Step>
            {index < steps.length - 1 && <div className="step-joint" />}
          </Fragment>
        )
      )}
    </div>
  );
}
